// src/pages/Reports/Reportrunsheet.js
import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch, faFileAlt, faSpinner, faTimesCircle,
  faFilePdf, faTruck, faUser, faMapMarkerAlt, faCalendarAlt
} from '@fortawesome/free-solid-svg-icons';
import html2pdf from 'html2pdf.js';

import BASE_URL from '../../api';

const API_URL = `${BASE_URL}/api/report/reportrunsheet`;
const CUSTOMER_API_URL = `${BASE_URL}/api/master/customer`;

const ReportRunsheet = () => {
  const [form, setForm] = useState({ startdate: '', enddate: '', customerid: '' });
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingCust, setLoadingCust] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState([]);
  const printRef = useRef();

  const token = () => localStorage.getItem('token');

  /* FETCH CUSTOMERS ON LOAD */
  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        const res = await fetch(CUSTOMER_API_URL, {
          headers: { Authorization: `Bearer ${token()}` }
        });
        const json = await res.json();
        if (json.success) setCustomers(json.data || []);
      } catch (e) {
        console.error("Error fetching customers:", e);
      }
      setLoadingCust(false);
    };
    fetchCustomers();
  }, []);

  /* FETCH RUNSHEET REPORT */
  const fetchReport = async () => {
    const { startdate, enddate, customerid } = form;
    if (!startdate || !enddate || !customerid) {
      setError('Please select start date, end date, and customer');
      return;
    }

    setLoading(true);
    setError(null);
    setData([]);

    try {
      const res = await fetch(API_URL, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ startdate, enddate, customerid }),
      });

      const json = await res.json();
      if (!res.ok) throw new Error(json.message || 'Failed to fetch report');

      if (json.success) {
        setData(Array.isArray(json.data) ? json.data : []);
      } else {
        setError(json.message);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  /* DATA MAPPING HELPERS */
  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-IN') : '-';

  const getRunsheetDetails = (item) => {
    // Navigating the nested JSON structure provided in your response
    const ogm = item.ingoingManifestId?.outgoingManifestId;
    const route = ogm?.routeid;

    return {
      // Logic: If route hub exists, use it, otherwise check branchname
      route: route ? `${route.hub || 'Origin'} to ${ogm.desthubid?.branchname || 'Destination'}` : 'N/A',
      driver: ogm?.drivername || 'N/A',
      // Vehicle: check vehiclenumberText first, then fallback to nested vehicleno
      vehicle: ogm?.vehiclenumberText || ogm?.transportid?.vehicleno || 'N/A',
      manifestNo: ogm?.manifestno || 'N/A',
      status: item.status || 'N/A'
    };
  };

  const getCustomerName = () => {
    const cust = customers.find(c => c._id === form.customerid);
    return cust?.companyName || cust?.name || 'Selected Customer';
  };

  /* PDF GENERATION */
  const generatePDF = () => {
    const element = printRef.current;
    const customerName = getCustomerName();

    const opt = {
      margin: [0.5, 0.5, 0.5, 0.5],
      filename: `Runsheet_Report_${form.startdate}_to_${form.enddate}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: 'in', format: 'a4', orientation: 'landscape' }, // Landscape fits more columns
      pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
    };

    html2pdf().set(opt).from(element).save();
  };

  const hasData = data.length > 0;
  const selectedCustomerName = getCustomerName();

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-8">
      <div className="max-w-7xl mx-auto">

        {/* HEADER SECTION */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
          <div>
            <h1 className="text-3xl font-extrabold text-gray-900 flex items-center gap-3">
              <FontAwesomeIcon icon={faFileAlt} className="text-blue-600" />
              Runsheet Report
            </h1>
            <p className="text-gray-500 mt-1 text-sm">Generate and download delivery runsheet summaries.</p>
          </div>
          {hasData && (
            <button
              onClick={generatePDF}
              className="flex items-center gap-2 px-6 py-3 bg-red-600 text-white font-bold rounded-lg hover:bg-red-700 shadow-lg transition-all active:scale-95"
            >
              <FontAwesomeIcon icon={faFilePdf} /> Export PDF
            </button>
          )}
        </div>

        {/* ERROR MESSAGE */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 text-red-700 rounded-r-lg flex items-center gap-3 animate-pulse">
            <FontAwesomeIcon icon={faTimesCircle} /> {error}
          </div>
        )}

        {/* FILTERS CARD */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div className="flex flex-col gap-2">
              <label className="text-xs font-bold text-gray-500 uppercase">Start Date</label>
              <input
                type="date"
                value={form.startdate}
                onChange={e => setForm({ ...form, startdate: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
              />
            </div>
            <div className="flex flex-col gap-2">
              <label className="text-xs font-bold text-gray-500 uppercase">End Date</label>
              <input
                type="date"
                value={form.enddate}
                onChange={e => setForm({ ...form, enddate: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
              />
            </div>
            <div className="flex flex-col gap-2">
              <label className="text-xs font-bold text-gray-500 uppercase">Customer</label>
              <select
                value={form.customerid}
                onChange={e => setForm({ ...form, customerid: e.target.value })}
                disabled={loadingCust}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none bg-white transition-all disabled:bg-gray-100"
              >
                <option value="">-- Choose Customer --</option>
                {customers.map(c => (
                  <option key={c._id} value={c._id}>{c.companyName || c.name}</option>
                ))}
              </select>
            </div>
          </div>
          <button
            onClick={fetchReport}
            disabled={loading}
            className="w-full md:w-48 py-3 bg-blue-600 text-white font-bold rounded-lg hover:bg-blue-700 disabled:bg-gray-400 flex items-center justify-center gap-2 shadow-md"
          >
            {loading ? <FontAwesomeIcon icon={faSpinner} className="animate-spin" /> : <FontAwesomeIcon icon={faSearch} />}
            {loading ? 'Processing...' : 'Generate Report'}
          </button>
        </div>

        {/* ON-SCREEN DATA LISTING */}
        <div className="grid grid-cols-1 gap-4">
          {data.map(item => {
            const details = getRunsheetDetails(item);
            return (
              <div key={item._id} className="bg-white border border-gray-200 rounded-xl p-5 hover:shadow-md transition-shadow">
                <div className="flex flex-col md:flex-row justify-between border-b border-gray-100 pb-3 mb-4 gap-2">
                  <div className="flex items-center gap-3">
                    <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded text-sm font-bold uppercase tracking-wider">
                      {item.runsheetno}
                    </span>
                    <span className="text-gray-400 text-sm">|</span>
                    <span className="text-gray-600 text-sm flex items-center gap-1">
                      <FontAwesomeIcon icon={faCalendarAlt} className="text-gray-400" /> {formatDate(item.rundate)}
                    </span>
                  </div>
                  <div className={`text-xs font-bold px-3 py-1 rounded-full ${details.status.includes('Delivered') ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'
                    }`}>
                    {details.status}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="flex items-start gap-3">
                    <FontAwesomeIcon icon={faMapMarkerAlt} className="mt-1 text-gray-400" />
                    <div>
                      <p className="text-xs text-gray-400 uppercase font-bold">Route</p>
                      <p className="text-sm font-semibold text-gray-800">{details.route}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <FontAwesomeIcon icon={faUser} className="mt-1 text-gray-400" />
                    <div>
                      <p className="text-xs text-gray-400 uppercase font-bold">Driver Name</p>
                      <p className="text-sm font-semibold text-gray-800">{details.driver}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <FontAwesomeIcon icon={faTruck} className="mt-1 text-gray-400" />
                    <div>
                      <p className="text-xs text-gray-400 uppercase font-bold">Vehicle No</p>
                      <p className="text-sm font-semibold text-gray-800">{details.vehicle}</p>
                    </div>
                  </div>
                </div>
              </div>
            )
          })}

          {!loading && !hasData && !error && (
            <div className="bg-white border-2 border-dashed border-gray-200 rounded-2xl py-20 text-center text-gray-400">
              <FontAwesomeIcon icon={faFileAlt} size="3x" className="mb-4 opacity-20" />
              <p>No report data to display. Adjust your filters and click generate.</p>
            </div>
          )}
        </div>

        {/* HIDDEN PRINT TEMPLATE (Landscape PDF) */}
        <div style={{ display: 'none' }}>
          <div ref={printRef} style={{ padding: '30px', fontFamily: 'sans-serif' }}>
            <div style={{ textAlign: 'center', marginBottom: '30px' }}>
              <h1 style={{ margin: 0, color: '#ea580c', fontSize: '24px' }}>RUNSHEET SUMMARY REPORT</h1>
              <p style={{ margin: '5px 0', color: '#666' }}>{selectedCustomerName}</p>
              <p style={{ fontSize: '12px', color: '#999' }}>Period: {formatDate(form.startdate)} to {formatDate(form.enddate)}</p>
            </div>

            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
              <thead>
                <tr style={{ backgroundColor: '#f8fafc' }}>
                  <th style={tableHeaderStyle}>Runsheet No</th>
                  <th style={tableHeaderStyle}>Date</th>
                  <th style={tableHeaderStyle}>Route</th>
                  <th style={tableHeaderStyle}>Driver</th>
                  <th style={tableHeaderStyle}>Vehicle</th>
                  <th style={tableHeaderStyle}>Manifest</th>
                  <th style={tableHeaderStyle}>Status</th>
                </tr>
              </thead>
              <tbody>
                {data.map(item => {
                  const d = getRunsheetDetails(item);
                  return (
                    <tr key={item._id}>
                      <td style={tableCellStyle}>{item.runsheetno}</td>
                      <td style={tableCellStyle}>{formatDate(item.rundate)}</td>
                      <td style={tableCellStyle}>{d.route}</td>
                      <td style={tableCellStyle}>{d.driver}</td>
                      <td style={tableCellStyle}>{d.vehicle}</td>
                      <td style={tableCellStyle}>{d.manifestNo}</td>
                      <td style={tableCellStyle}>{d.status}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>

            <div style={{ marginTop: '40px', fontSize: '10px', color: '#999', borderTop: '1px solid #eee', paddingTop: '10px' }}>
              Report generated on {new Date().toLocaleString()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/* INLINE PDF STYLES */
const tableHeaderStyle = {
  border: '1px solid #e2e8f0',
  padding: '10px',
  textAlign: 'left',
  fontWeight: 'bold',
  textTransform: 'uppercase',
  fontSize: '10px'
};

const tableCellStyle = {
  border: '1px solid #e2e8f0',
  padding: '8px',
  color: '#334155'
};

export default ReportRunsheet;